import type { TDateISO } from "~/@types/TDateISO";
import type { JobStatus } from "~produce/production/schedule/import/helpers/types";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface LocalOverrides {
	customerGroupId?: null | string;
	customerGroupName?: null | string;
	customerId?: string;
	customerName?: null | string;
	effectivePlantsPerPot?: number;
	isPlantsPerPotOverridden?: boolean;
	isQuantityOverridden?: boolean;
	isYieldOverridden?: boolean;
	jobStatus?: JobStatus;
	locationId?: string;
	locationName?: string;
	parentLocationName?: null | string;
	productionPlanName?: string;
	quantity?: number;
	siteName?: string;
	transplantDate?: string;
	weekReadyDate?: TDateISO;
	weekStartDate?: TDateISO;
	yield?: number;
}

export interface ProductionScheduleState {
	autoWidth: boolean;
	detailActiveTab: number;
	detailPinned: boolean;
	localOverrides: Record<string, LocalOverrides>;
	selectedJobId: string | null;
	selectedRows: string[];
	showArchived: boolean;
}

const initialState: ProductionScheduleState = {
	autoWidth: false,
	detailActiveTab: 0,
	detailPinned: true,
	localOverrides: {},
	selectedJobId: null,
	selectedRows: [],
	showArchived: false
};

const clearLocalOverrideFields = (
	state: ProductionScheduleState,
	jobIds: string[],
	fields: (keyof LocalOverrides)[]
): void => {
	for (const jobId of jobIds) {
		const override = state.localOverrides[jobId];
		if (!override) continue;
		for (const field of fields) delete override[field];
		if (Object.keys(override).length === 0) delete state.localOverrides[jobId];
	}
};

export const productionScheduleSlice = createSlice({
	initialState,
	name: "productionSchedule",
	reducers: {
		clearLocalCustomerGroupOverride: (state, action: PayloadAction<string[]>) => {
			clearLocalOverrideFields(state, action.payload, ["customerGroupId", "customerGroupName"]);
		},
		clearLocalCustomerOverride: (state, action: PayloadAction<string[]>) => {
			clearLocalOverrideFields(state, action.payload, ["customerId", "customerName"]);
		},
		clearLocalOverrides: (state, action: PayloadAction<string[]>) => {
			for (const jobId of action.payload) {
				delete state.localOverrides[jobId];
			}
		},
		clearSelectedRows: (state) => {
			state.selectedRows = [];
		},
		setAutoWidth: (state, action: PayloadAction<boolean>) => {
			state.autoWidth = action.payload;
		},
		setDetailActiveTab: (state, action: PayloadAction<number>) => {
			state.detailActiveTab = action.payload;
		},
		toggleDetailPinned: (state) => {
			state.detailPinned = !state.detailPinned;
		},
		setLocalOverride: (state, action: PayloadAction<{ jobId: string; overrides: Partial<LocalOverrides> }>) => {
			const { jobId, overrides } = action.payload;
			state.localOverrides[jobId] = { ...state.localOverrides[jobId], ...overrides };
		},
		setSelectedJobId: (state, action: PayloadAction<string | null>) => {
			state.selectedJobId = action.payload;
		},
		setSelectedRows: (state, action: PayloadAction<string[]>) => {
			state.selectedRows = action.payload;
		},
		setShowArchived: (state, action: PayloadAction<boolean>) => {
			state.showArchived = action.payload;
		}
	}
});

export const {
	clearLocalCustomerGroupOverride,
	clearLocalCustomerOverride,
	clearLocalOverrides,
	clearSelectedRows,
	setAutoWidth,
	setDetailActiveTab,
	setLocalOverride,
	setSelectedJobId,
	setSelectedRows,
	setShowArchived,
	toggleDetailPinned
} = productionScheduleSlice.actions;

export default productionScheduleSlice.reducer;
